import { useState, useEffect, useRef, useMemo, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  TransactionHeader,
  TransactionDetailsCard,
  SwapCurrencyCards,
  TransactionStatusProgress,
  TransactionInfoCard,
  copyToClipboard,
  formatDate,
  getSwapSteps,
  getSwapStepState,
  getSwapProgressValue,
  LoadingPage,
  pendingFilter,
  Button,
  canApproveTransaction,
  type SwapStatus,
  CompanyRoleBadge,
  Badge,
  FlaggedBadgeIcon,
  ApproveMfaDialog,
  RejectConfirmationDialog,
  TransactionError,
  FallbackCompanyIcon,
  UserRole,
  buildSwapSummaryDisplay,
  parseFormattedAmount,
} from '@valiant/ui-shared'
import { maskSwapStatus } from '../utils/swapStatus'
import { toast } from 'sonner'
import {
  useGetUserSwapTransactionQuery,
  useGetCompanyQuery,
  useRejectSwapMutation,
  useApproveSwapMutation,
} from '../generated/graphql'
import { useNavigation } from '../contexts/NavigationContext'
import { Check, Loader2, X } from 'lucide-react'
import { MainLayout } from '../components/layout/MainLayout'
import { useAuth } from '../AuthProvider'
import { environment } from '../environment'

export function SwapTransaction() {
  const { transactionId } = useParams()
  const navigate = useNavigate()
  const [hasInitiallyLoaded, setHasInitiallyLoaded] = useState(false)

  // Check if we're in swap mode to hide admin actions
  const { isSwapMode } = useNavigation()
  const { auth } = useAuth()
  const userRole = auth?.role
  // GraphQL query for swap transaction (user can only see their own)
  const [{ data: swapData, fetching: isLoading, error }, refetch] = useGetUserSwapTransactionQuery({
    variables: { id: transactionId || '' },
    pause: !transactionId,
  })

  const [{ data: companyData, fetching: companyLoading, error: companyError }] = useGetCompanyQuery(
    {
      variables: { companyId: swapData?.getSwap?.companyId || '' },
      pause: !swapData?.getSwap?.companyId,
    },
  )

  const swapTransaction = swapData?.getSwap
  const swapStatus = maskSwapStatus(
    swapTransaction?.status,
    swapTransaction?.isExternalApi,
  ) as SwapStatus

  const canShowActions = canApproveTransaction(
    swapTransaction?.status as SwapStatus,
    false,
    'TRADE',
  )
  const isFlagged = swapTransaction?.isFlagged

  // Track when initial data has been loaded
  useEffect(() => {
    if (swapTransaction && !hasInitiallyLoaded) {
      setHasInitiallyLoaded(true)
    }
  }, [swapTransaction, hasInitiallyLoaded])

  // GraphQL mutations (no admin actions)
  const [, approveSwap] = useApproveSwapMutation()
  const [, rejectSwap] = useRejectSwapMutation()
  const [isApproving, setIsApproving] = useState(false)
  const [isRejecting, setIsRejecting] = useState(false)
  const [showMfaDialog, setShowMfaDialog] = useState(false)
  const [showRejectDialog, setShowRejectDialog] = useState(false)

  // Ref to store the interval ID for cleanup
  const refetchIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  // Auto-refetch for non-finalized swaps (excluding requested state)
  useEffect(() => {
    // Clear any existing interval
    if (refetchIntervalRef.current) {
      clearInterval(refetchIntervalRef.current)
      refetchIntervalRef.current = null
    }

    // Check if we should refetch based on status
    const shouldRefetch = swapTransaction && swapStatus && pendingFilter.includes(swapStatus)

    if (shouldRefetch) {
      // Refetch every 5 seconds for active/processing states
      refetchIntervalRef.current = setInterval(() => {
        refetch({ requestPolicy: 'network-only' })
      }, 5000)
    }

    // Cleanup function
    return () => {
      if (refetchIntervalRef.current) {
        clearInterval(refetchIntervalRef.current)
        refetchIntervalRef.current = null
      }
    }
  }, [swapTransaction?.status, swapTransaction, refetch])

  const handleApprove = () => {
    setShowMfaDialog(true)
  }

  const handleReject = () => {
    setShowRejectDialog(true)
  }

  const handleCopy = async (text: string, _field: string) => {
    await copyToClipboard(text)
  }

  const handleApproveSwap = useCallback(
    async (proof: { mfaCode?: string; passkeyApproval?: string }) => {
      setIsApproving(true)
      try {
        const result = await approveSwap({ input: { swapId: transactionId!, ...proof } })
        if (result.error) {
          throw new Error(result.error.message)
        }
        toast.success('Swap approved successfully')
        setShowMfaDialog(false)
      } catch (error) {
        toast.error('Failed to approve swap')
        console.error('Error approving swap:', error)
      } finally {
        setIsApproving(false)
      }
    },
    [approveSwap, transactionId],
  )

  const handleRejectSwap = useCallback(async () => {
    setIsRejecting(true)
    try {
      const result = await rejectSwap({
        input: { swapId: transactionId!, reason: 'Rejected by admin' },
      })
      if (result.error) {
        throw new Error(result.error.message)
      }
      toast.success('Swap rejected successfully')
    } catch (error) {
      toast.error('Failed to reject swap')
      console.error('Error rejecting swap:', error)
    } finally {
      setIsRejecting(false)
      setShowRejectDialog(false)
    }
    setIsRejecting(false)
  }, [rejectSwap, transactionId])

  const modalsComponent = useMemo(() => {
    return (
      <>
        <ApproveMfaDialog
          open={showMfaDialog}
          onOpenChange={setShowMfaDialog}
          onConfirm={handleApproveSwap}
          isSubmitting={isApproving}
          title="Approve Swap"
          apiUrl={environment.API_URL}
          token={auth?.token}
          action="approveSwap"
          targetKey={transactionId}
        />
        <RejectConfirmationDialog
          open={showRejectDialog}
          onOpenChange={setShowRejectDialog}
          onConfirm={handleRejectSwap}
          isSubmitting={isRejecting}
          title="Reject Transaction"
          description="Are you sure you want to reject this swap transaction? This action cannot be undone."
        />
      </>
    )
  }, [
    showMfaDialog,
    showRejectDialog,
    isApproving,
    isRejecting,
    auth?.token,
    handleApproveSwap,
    handleRejectSwap,
  ])

  const badges = useMemo(() => {
    return [
      {
        node: <CompanyRoleBadge tradeType={companyData?.getCompany?.tradeType || ''} />,
        direction: 'LEFT',
      },
      {
        node:
          userRole === UserRole.Trader ? null : (
            <Badge variant="outline" className="text-xs ">
              {swapTransaction?.group}
            </Badge>
          ),
        direction: 'LEFT',
      },
    ]
  }, [companyData, swapTransaction, userRole])

  // Loading state - only show on first load
  if ((isLoading || companyLoading) && !hasInitiallyLoaded) {
    return <LoadingPage />
  }

  // Error state
  if (error || companyError) {
    return <TransactionError message={error?.message || companyError?.message || ''} />
  }

  // Not found state
  if (!swapTransaction) {
    return (
      <TransactionError
        message="The swap transaction you're looking for doesn't exist or you don't have permission to view it."
        title="Transaction Not Found"
      />
    )
  }

  return (
    <MainLayout
      breadcrumbs={[
        {
          label: swapTransaction.company,
          onClick: () => navigate(`/companies/${swapTransaction.companyId}`),
          imageUrl: swapTransaction.companyImageUrl || '',
          fallbackImage: <FallbackCompanyIcon />,
        },
      ]}
      badges={badges}
      modalsComponent={modalsComponent}
      subBarOverride={
        <TransactionHeader
          company={swapTransaction.company}
          companyImageUrl={swapTransaction.companyImageUrl || ''}
          companyTradeType={companyData?.getCompany?.tradeType || ''}
          transactionId={swapTransaction.id}
          status={swapStatus}
          transactionType="TRADE"
          isFlagged={swapTransaction.isFlagged}
          isExternalApi={swapTransaction.isExternalApi}
          onApprove={() => setShowMfaDialog(true)}
          onReject={() => setShowRejectDialog(true)}
          isApproving={isApproving}
          isRejecting={isRejecting}
          showActions={!isSwapMode} // Hide approve/reject actions in swap mode
        />
      }
    >
      {/* Transaction Overview Header */}
      <div className="space-y-4 mb-6 flex items-center justify-between md:block">
        <h2 className="hidden md:block text-xl font-semibold">Transaction Overview</h2>
        <h3 className="text-2xl md:text-4xl font-normal md:font-semibold md:text-main flex gap-2 items-center mb-0">
          <div className="md:hidden">{isFlagged ? <FlaggedBadgeIcon /> : null}</div>
          Swap
        </h3>
        {canShowActions && (
          <div className="flex gap-2 md:hidden">
            <Button
              variant="main_action"
              onClick={handleApprove}
              className="bg-main/95 hover:bg-main h-9"
              disabled={isApproving}
            >
              {isApproving ? (
                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
              ) : (
                <Check className="w-4 h-4 mr-1" />
              )}
              {isApproving ? 'Approving...' : 'Approve'}
            </Button>
            <Button variant="outline" onClick={handleReject} className="h-9" disabled={isRejecting}>
              {isRejecting ? (
                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
              ) : (
                <X className="w-4 h-4 mr-1" />
              )}
              {isRejecting ? 'Rejecting...' : 'Reject'}
            </Button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Column 1 - From/To Cards and Transaction Status */}
        <div className="space-y-6">
          <SwapCurrencyCards
            fromMint={{
              ...swapTransaction.fromMint,
              address: swapTransaction.fromMint.address || '',
              image: swapTransaction.fromMint.image || undefined,
            }}
            toMint={{
              ...swapTransaction.toMint,
              address: swapTransaction.toMint.address || '',
              image: swapTransaction.toMint.image || undefined,
            }}
            fromAmount={swapTransaction.fromAmount}
            toAmount={swapTransaction.toAmount}
            status={swapStatus}
          />

          <TransactionStatusProgress
            status={swapStatus}
            steps={getSwapSteps()}
            getStepState={(index) => getSwapStepState(index, swapStatus)}
            getProgressValue={getSwapProgressValue}
          />
        </div>

        {/* Column 2 - Info and Details */}
        <div className="space-y-6">
          <TransactionInfoCard
            user={swapTransaction.requestedBy || '-'}
            company={swapTransaction.company}
            group={userRole === UserRole.Trader ? undefined : swapTransaction.group}
            decisionBy={swapTransaction.decisionBy || '-'}
            transactionId={swapTransaction.id}
            creationDate={formatDate(swapTransaction.createdAt)}
            decisionDate={swapTransaction.decisionAt ? formatDate(swapTransaction.decisionAt) : '-'}
            finalizedDate={
              swapTransaction.completedAt ? formatDate(swapTransaction.completedAt) : '-'
            }
            onCopy={handleCopy}
            swapSummary={buildSwapSummaryDisplay({
              fromAmount: parseFormattedAmount(swapTransaction.fromAmount),
              toAmount: parseFormattedAmount(swapTransaction.toAmount),
              swapGroupBps: swapTransaction.swapGroupBps,
              swapValiantBps: swapTransaction.swapValiantBps,
              fromDecimals: swapTransaction.fromMint.displayDecimals ?? 6,
              toDecimals: swapTransaction.toMint.displayDecimals ?? 6,
              fromSymbol: swapTransaction.fromMint.symbol,
              toSymbol: swapTransaction.toMint.symbol,
            })}
          />

          <TransactionDetailsCard
            title="Details"
            fields={[
              {
                label: 'Withdrawal Transaction Hash',
                value: swapTransaction.pushoutWithdrawalTxHash || '-',
                fieldKey: 'hash',
              },
              {
                label: 'Withdrawal Address',
                value: swapTransaction.whitelistedAddress,
                fieldKey: 'address',
              },
            ]}
            onCopy={handleCopy}
            chain={swapTransaction.toMint.chain}
          />
        </div>
      </div>
    </MainLayout>
  )
}
